import { IDP_GOOGLE_OAUTH_CALLBACK } from 'Pages/help/constants';
import { SSOButtonOptionsByProvider, SSOProvider } from 'Shared/components/design/SSOButton/constants';
import { navigateToMemberPage } from 'Shared/services/routingService';
import { postFacebookSignIn, postGoogleSignIn } from 'Shared/services/userActivation/api/idpApiService';
import { capitalize } from 'Shared/utils/formatUtils';

/**
 * Initializes the Google SSO code client
 */
export const initializeGoogleSSO = (errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void): void => {
  if (window.google?.accounts?.oauth2) {
    return window.google.accounts.oauth2.initCodeClient({
    client_id   : process.env.PUBLIC_GOOGLE_SSO_CLIENT_ID || '',
      scope       : 'openid email profile',
      ux_mode     : 'popup',
      redirect_uri: `${process.env.REACT_APP_IDP_BASE_URL}${IDP_GOOGLE_OAUTH_CALLBACK}`,
      callback    : (tokenResponse: { code?: string; error?: string }) => {
        isLoadingCallback?.(true);

        if (tokenResponse.error) {
          errorCallback?.(SSOProvider.GOOGLE);
          isLoadingCallback?.(false);
          return;
        }

        if (tokenResponse.code) {
          postGoogleSignIn(tokenResponse.code).then(() => {
            navigateToMemberPage();
          }).catch(() => {
            errorCallback?.(SSOProvider.GOOGLE);
          }).finally(() => {
            isLoadingCallback?.(false);
          });
          return;
        }

        isLoadingCallback?.(false);
        errorCallback?.(SSOProvider.GOOGLE);
      },
    });
  }
};

/**
 * Load Google OAuth Client Script
 * @returns Promise<void>
 */
export const loadGoogleSDK = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById('google-oauth-client')) {
      resolve();
      return;
    }

    const script       = document.createElement('script');
          script.id    = 'google-oauth-client';
          script.src   = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;

    // resolve promise on load
    script.onload = () => {
      resolve();
    };

    // reject promise on error
    script.onerror = (err) => {
      reject(err);
    };

    document.body.appendChild(script);
  });
};

/**
 * Load Facebook SDK
 * @returns Promise<void>
 */
export const loadFacebookSDK = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    // check if the script is already injected, if so, resolve
    if (document.getElementById('facebook-jssdk')) {
      resolve();
      return;
    }

    // set up the global callback that Facebook calls once it's loaded
    window.fbAsyncInit = function () {
      window.FB.init({
        appId  : process.env.PUBLIC_FACEBOOK_APP_ID || '',
        cookie : true,
        xfbml  : false,
        version: 'v17.0',
      });

      resolve();
    };

    // create the script element
    const script       = document.createElement('script');
          script.id    = 'facebook-jssdk';
          script.src   = 'https://connect.facebook.net/en_US/sdk.js';
          script.async = true;
          script.defer = true;

    // if there's a network error loading the script, reject
    script.onerror = (err) => {
      reject(err);
    };

    document.body.appendChild(script);
  });
};

/**
 * Handles Facebook oauth response (FB.login()) and passes it to IDP API
 */
export const handleFacebookResponse = (response: { status: string; authResponse?: { accessToken: string } }, errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void): void => {
  // if the user is 'connected', we have a valid FB authResponse
  if (response.status === 'connected' && response.authResponse?.accessToken) {
    isLoadingCallback?.(true);
    const token = response.authResponse.accessToken;
    postFacebookSignIn(token)
      .then(() => {
        navigateToMemberPage();
      })
      .catch(() => {
        errorCallback?.(SSOProvider.FACEBOOK);
      }).finally(() => {
        isLoadingCallback?.(false);
      });
  }
};

/**
 * Initiate Facebook Login (pop-up).
 */
export const handleFacebookLogin = (errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void) => {
  if (!window.FB) {
    errorCallback?.(SSOProvider.FACEBOOK);
    return;
  }

  // trigger Facebook login popup and handle the response
  window.FB.login((response) => handleFacebookResponse(response, errorCallback, isLoadingCallback), { scope: 'email, public_profile' });
};

/**
 * Determines the SSO provider name
 * @param provider
 * @returns
 */
export const getSSOProviderName = (ssoProvider: SSOProvider): string => {
  if (ssoProvider === SSOProvider.GOOGLE) {
    return SSOButtonOptionsByProvider[SSOProvider.GOOGLE].name;
  }
  if (ssoProvider === SSOProvider.FACEBOOK) {
    return SSOButtonOptionsByProvider[SSOProvider.FACEBOOK].name;
  }

  return '';
};

export const getSSOLoginUrl = (provider: SSOProvider) => {
  if (Object.values(SSOProvider).includes(provider)) {
    return `${process.env.REACT_APP_MONOLITH_BASE_URL}/${provider}/login`;
  }

  throw new Error('Invalid SSO provider');
};

export const openSSOProviderWindow = (provider: SSOProvider): WindowProxy | null => {
  const url = getSSOLoginUrl(provider);
  const signUpWindow = window.open(url, `Login in using ${capitalize(provider)}`, "width=1000,height=700,toolbar=0,scrollbars=0,status=0,resizable=0,location=0,menuBar=0");
  signUpWindow?.focus();
  return signUpWindow;
};

